"use client"

import * as React from "react"

import { Badge } from "@/components/ui/badge"
import { UsdcIcon } from "@/components/ui/icons"
import { activeGateBand, buildGateBands, MAX_GROUP_SIZE } from "@/lib/arena/v2-types"
import { cn } from "@/lib/utils"

/* =============================================================================
   Arena V2 shared primitives
   -----------------------------------------------------------------------------
   Six pieces every Arena surface is built from, plus the class constants that
   give the whole feature one visual system. Rules folded in here so the
   consuming surfaces cannot drift:

   - One noun for a participant: "lineups". Never members, players or entrants.
   - THE MONEY IS THE INTERFACE. The decisive figure on a screen is the largest
     and brightest thing on it, and its colour states what happened to it. See
     ARENA_MONEY / ARENA_MONEY_TONE below; everything else steps down from it.
   - THE HOUSE PALETTE, as written down in the banner system: pink is action and
     new money, gold is celebration and value, green is REFERRAL and network and
     belongs to nothing in Arena. Arena used --win on every paid amount, which is
     the generic success-is-green default rather than a brand decision, and it
     collided with --creator seven degrees away. Arena is now pink and gold only.
     Gold is the colour of value: a paid amount, a first place, the top-score
     branch. Pink is the colour of action: the primary button, and the
     perfect-lineup branch that the product guarantees. Nothing else is tinted.
   - No uppercase blocks, no letter-spacing above tracking-tight, no mono in
     chrome. All three read casino, and the banner system rules them out by name.
     ARENA_EYEBROW used to be all three at once on every screen in the feature.
   - Pixter Terminal is claimed for the Arena score and nothing else. A settled
     score is the record and gets the full 44px; a projected one is provisional
     and sits a step down at 34px. Score cells inside a long table stay in Inter
     tabular-nums so 64 rows are comparable down a column.
   - Money is the app's existing money treatment (Inter, tabular-nums), matching
     EntryFeeSelector, at font-black only where it is the hero figure. Open
     Sauce is for titles only.
   - Weights are font-normal / font-semibold / font-black only. Open Sauce Two
     has no 700, and Pixter Terminal has only 700.
   - No number here is invented. Group-size thresholds are server-owned values
     passed in as props; this file ships no table of money rules.
   ============================================================================= */

/* =============================================================================
   The shared class system
   ============================================================================= */

/**
 * Two surface levels, and only two. Every Arena card used to be the same
 * "border-grey-800 bg-grey-950" string repeated in five files, which meant no
 * card was primary and the whole feature read as one flat sheet.
 *
 * `hero` is the one raised object on a screen: the prize ledger before
 * settlement, the result after it. It is lighter at the top than the page,
 * carries a brighter hairline and a 1px inner light line, and it is the only
 * place a gradient is allowed.
 * `heroFlush` is the SAME raised identity for a hero that cannot take the
 * hero's own padding: the group board, which is a scrolling table with its own
 * row padding, a sticky column header and a sticky footer. It keeps the
 * brighter hairline and the lifted fill and drops the p-5 and the gradient,
 * because a vertical gradient behind a list that scrolls under it bands against
 * the rows. It is a variant of one treatment, not a third treatment.
 * `card` is everything else, and it recedes: page-dark fill, quiet border.
 *
 * The inner light line came off: an inset shine is a bevel, and the banner
 * system rules out bevels, drop shadows and colored frames by name. The lift
 * here is carried by fill and hairline alone.
 */
const ARENA_SURFACE = {
  hero: "rounded-2xl border border-grey-700 bg-gradient-to-b from-grey-800 to-grey-950 p-5",
  heroFlush: "rounded-2xl border border-grey-700 bg-grey-900",
  card: "rounded-2xl border border-grey-800 bg-grey-950 p-4",
} as const

/**
 * The house treatment for a card that matters, used by player cards, match
 * cards, album cards, bundle cards and the airdrop modal. Arena reached for
 * custom borders instead and looked foreign next to the rest of the product.
 *
 * Drop it inside a `relative` shell as a sibling before the content, and give
 * the content its own `relative` so it sits above. Two moments earn it: the
 * result that was actually paid, and the ledger at the moment the group locks.
 * Anywhere else it is a texture, which is what the standing rules call slop.
 */
function ArenaBrandedStripes({
  opacity = "0.07",
  fade = "0.9",
}: {
  opacity?: string
  fade?: string
}) {
  return (
    <span
      aria-hidden
      className="branded-stripes pointer-events-none absolute inset-0 rounded-2xl"
      style={{ "--stripe-opacity": opacity, "--stripe-fade": fade } as React.CSSProperties}
    />
  )
}

/**
 * The money scale. One `hero` per screen, and it is the largest thing on that
 * screen by a wide margin; `major` is the supporting figure; `row` and `inline`
 * are the ledger sizes that have to stay comparable down a column.
 *
 * Face: the two display sizes take Open Sauce Two, the brand face, which is
 * what the landing and every other branded surface uses for a figure this big.
 * `row` and `inline` deliberately stay on the UI face: they live inside
 * scrolling columns up to 64 rows deep, where reliable tabular figures matter
 * more than brand, and that is the convention the app's other tables follow.
 */
const ARENA_MONEY = {
  hero: "font-open-sauce text-[34px] font-black leading-none tracking-[-0.03em] tabular-nums",
  major: "font-open-sauce text-[22px] font-semibold leading-none tracking-[-0.02em] tabular-nums",
  row: "text-[15px] font-semibold tabular-nums tracking-tight",
  inline: "text-[13px] font-semibold tabular-nums tracking-tight",
} as const

/**
 * What happened to that money, carried by colour alone and consistently
 * everywhere. A refund is not a win and never takes `paid`.
 */
const ARENA_MONEY_TONE = {
  /** Quoted, or still live. */
  live: "text-white",
  /**
   * Won and paid. Gold, not --win: green is the referral colour in this product
   * and green-means-success is a default, not a brand. Gold is what the banner
   * system already spends on a celebratory value moment, and it is the same hue
   * that marks first place on the group board, so a paid amount and the rank
   * that earned it are visibly the same idea.
   */
  paid: "text-gold",
  /** Dead, struck, refused. */
  dead: "text-grey-300 line-through decoration-grey-500",
} as const

/** Money treatment in one call: size, then state. */
function arenaMoney(
  size: keyof typeof ARENA_MONEY,
  tone: keyof typeof ARENA_MONEY_TONE = "live",
  className?: string
): string {
  return cn(ARENA_MONEY[size], ARENA_MONEY_TONE[tone], className)
}

/**
 * The two prize branches, which until now showed up only as a 2px rule and
 * were the same pink either way. `rule` is that rule; `tint` is for a branch
 * eyebrow or the MULTIPLE, never for an amount.
 */
const ARENA_BRANCH = {
  /** Perfect lineup: the branch the product guarantees, on the action accent. */
  perfect: { rule: "bg-primary", tint: "text-primary" },
  /**
   * Top score: the contested branch, on gold. It was mint, which put a second
   * green on a screen that already paid in green and sat seven degrees from it.
   */
  top: { rule: "bg-gold", tint: "text-gold" },
} as const

/**
 * The eyebrow above an Arena block. Chrome: it never competes.
 *
 * It was 10px mono, uppercase, at 0.2em tracking, which is three of the banner
 * system's named bans stacked into one token and repeated on every screen in
 * the feature. That is the single loudest reason Arena read as a casino product
 * instead of this one. It is now the brand face, sentence case, tracking-tight,
 * and legible: the label of a block, not a marquee.
 */
const ARENA_EYEBROW =
  "font-open-sauce text-[11px] font-semibold tracking-tight text-white-700"

/**
 * The quiet text-only action: "See the whole group", "How Arena works", "Show
 * all 64". Same face and rhythm as the eyebrow, one step up in weight of colour
 * because it is pressable. Six files had each written their own version of this
 * at a different size, tracking and grey, which is most of why the lifecycle
 * read as six screens by six designers rather than one product.
 */
const ARENA_TEXT_ACTION =
  "font-open-sauce text-[12px] font-semibold tracking-tight text-white-700 transition-colors hover:text-white"

/** $1,234.50, two decimals always, matching the slip's money treatment. */
function formatUsd(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** "intermediate" and "top-tier" become "Intermediate" and "Top tier". */
function toSentenceCase(value: string): string {
  const spaced = value.replace(/[-_]+/g, " ").trim()
  if (!spaced) return ""
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/* =============================================================================
   ArenaFieldCard
   ============================================================================= */

/* HEAVY-PRO LANGUAGE (2026-08-03 restyle test): the full-bleed colour fields
   are retired. Every field is near-black; the state's hue survives only as a
   corner glow behind the figure and in the accents the type already carries.
   The base keeps a trace of the hue in its greys so the three states still
   read differently across the room without any of them being a colour block. */
const ARENA_FIELD = {
  /** The win. Reserved for money that was actually paid. */
  gold: {
    background:
      "linear-gradient(118deg, hsl(45 10% 4%) 0%, hsl(45 9% 7%) 55%, hsl(45 10% 10%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(var(--gold) / 0.30) 0%, transparent 52%)",
    ink: "light",
  },
  /** The live, contested, in-progress states. Dark brand pink on the black
      field: the hue survives in the greys and the corner glow, never as a
      colour block. (USDC blue was tried 2026-08-03 and dropped.) */
  pink: {
    background:
      "linear-gradient(118deg, hsl(335 24% 4%) 0%, hsl(335 22% 7%) 55%, hsl(335 20% 10%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(var(--primary-0) / 0.26) 0%, transparent 52%)",
    ink: "light",
  },
  /** The reader's own group mid-lifecycle: locked, live, settling. The one
      field that wears the brand pink as a colour block rather than a trace —
      deep at the copy corner, vivid under the figure. */
  brand: {
    background:
      "linear-gradient(118deg, hsl(335 55% 18%) 0%, hsl(335 65% 30%) 45%, hsl(335 78% 44%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(var(--primary-0) / 0.40) 0%, transparent 52%)",
    ink: "light",
  },
  /** The settled win. Same construction as the brand field, in the win green,
      so the result is the same object the reader watched all lifecycle. */
  win: {
    background:
      "linear-gradient(118deg, hsl(155 55% 16%) 0%, hsl(153 60% 26%) 45%, hsl(150 62% 36%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(150 65% 45% / 0.40) 0%, transparent 52%)",
    ink: "light",
  },
  /** Nothing has happened yet, or nothing was paid. */
  dark: {
    background:
      "linear-gradient(118deg, hsl(0 0% 3%) 0%, hsl(0 0% 6%) 55%, hsl(0 0% 9%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(var(--white-100)) 0%, transparent 52%)",
    ink: "light",
  },
} as const

/** The one accent each field is allowed: the corner bloom under the brand mark. */
const ARENA_FIELD_ACCENT = {
  gold: { bloom: "hsl(var(--gold) / 0.14)" },
  pink: { bloom: "hsl(var(--primary-0) / 0.16)" },
  brand: { bloom: "hsl(var(--primary-0) / 0.35)" },
  win: { bloom: "hsl(150 65% 45% / 0.35)" },
  dark: { bloom: "hsl(0 0% 100% / 0.05)" },
} as const

/** The brand mark's outline, from public/picks-mark.svg. Inlined because the
    edge trace needs the path itself: a mask can tint the mark but cannot run
    a light along its contour. One source of truth for the shape stays the SVG
    file; this is its path verbatim. */
const PICKS_MARK_PATH =
  "M24.3435 26.2618C24.559 26.3092 24.6638 26.5545 24.5492 26.7429L22.0537 30.843C21.9764 30.97 21.8221 31.0274 21.6805 30.9818L18.0184 29.8024C17.9448 29.7787 17.8822 29.7292 17.8421 29.6631L14.8521 24.7335C14.7055 24.4918 14.9198 24.1921 15.1959 24.2528L24.3435 26.2618ZM27.5723 21.7849C27.5039 21.8971 27.374 21.9564 27.2444 21.9345L11.6594 19.3037C11.5675 19.2882 11.4868 19.2335 11.4383 19.1539L8.71924 14.6873C8.58521 14.4671 8.75181 14.1866 9.00927 14.199L22.4215 14.8443C22.5176 14.8489 22.6107 14.8103 22.6752 14.739L29.1237 7.62029C29.3108 7.41366 29.1642 7.08301 28.8854 7.08301H10.0811C9.98691 7.08301 9.89743 7.12434 9.83635 7.19609L6.80359 10.7584C6.66169 10.9251 6.39832 10.9043 6.28435 10.7174L0.047105 0.48883C-0.0835217 0.274612 0.0706639 0 0.321567 0H40.2915C40.5425 0 40.6967 0.274685 40.566 0.4889L27.5723 21.7849Z"

/**
 * The mark in the ground: a still silhouette one step lighter than the field,
 * with a hairline edge so the shape holds where the fill dies into the glow.
 * No motion — the card's life is its numbers, not its wallpaper.
 */
function ArenaMark({
  className,
  /** Extra alpha on both the fill and the edge trace. The brand field runs
      5 points louder: on the vivid pink the quiet values disappear. */
  alphaBoost = 0,
}: {
  className?: string
  alphaBoost?: number
}) {
  return (
    <svg viewBox="0 0 40.7 31" fill="none" aria-hidden className={className}>
      <path d={PICKS_MARK_PATH} fill={`hsl(211 20% 90% / ${0.032 + alphaBoost})`} />
      <path
        d={PICKS_MARK_PATH}
        fill="none"
        stroke={`hsl(211 20% 90% / ${0.058 + alphaBoost})`}
        strokeWidth="0.3"
      />
    </svg>
  )
}

/** Type on the gold strip: 1:1 the board banners' ribbon (mono 8.5px, 0.20em). */
const ARENA_STRIP_TEXT =
  "font-mono text-[8.5px] font-medium uppercase tracking-[0.20em] text-grey-950"

interface ArenaFieldCardProps extends React.ComponentProps<"div"> {
  tone: keyof typeof ARENA_FIELD
  /** Small label pinned to the top-left. */
  eyebrow: string
  /**
   * Gold header strip, the settled banner's own device: where this object
   * belongs on the left, its ledger state on the right. With it the card
   * squares its top corners against the strip.
   */
  strip?: { left: string; right: React.ReactNode }
  /**
   * A dark pill above the headline with a gold dot: the card's live status in
   * two or three words ("Live now"). For the states where something is still
   * moving; a settled card has nothing to pulse about.
   */
  chip?: string
  /** Thin rail, optionally with a fact at either end, pinned to the card's foot. */
  progress?: {
    value: number
    max: number
    left?: string
    right?: string
    /**
     * A marker on the rail at the value that matters before the fill gets
     * there — the minimum for the group to run. It explains itself on hover
     * and disappears once the fill passes it: a met threshold is not news.
     */
    threshold?: { value: number; explain: string }
  }
  /** Dark foot bar under the field: a rule of the game left (a node, so a
      host can put small actions there), currency right. `usdcIcon: false`
      drops the coin when the right slot is not about money. */
  foot?: { left: React.ReactNode; right: React.ReactNode; usdcIcon?: boolean }
  /** The brand mark, oversized and barely-there, in the field's lower right. */
  brandMark?: boolean
  /** Two words at most, on the bottom baseline. Ends with a full stop. */
  headline: string
  /** One line under the headline. The only sentence on the card. */
  caption?: string
  /**
   * The one number this state is about, rendered as the artwork on the right:
   * a payout, a countdown, a running score, a group size. Omit and the copy
   * zone takes the full width.
   */
  figure?: string
  /** A word under the figure, e.g. "Paid", "Projected". */
  figureCaption?: string
  /** True while the figure can still change, which tints it and marks it live. */
  figureLive?: boolean
  /** Plays the arrival beat on the figure. */
  reveal?: boolean
  /**
   * A sweeping radar behind the figure, for the one state that is genuinely a
   * search in progress. Rings and a sweep only: no blips, ever. A blip would be
   * a claim about another lineup, and while matchmaking runs there is nothing
   * true to say about anyone else. The sweep says "still looking", which is the
   * only fact this card has.
   */
  radar?: boolean
}

/**
 * ONE object, six states. Every Arena screen used to open with something
 * different, a heading on one, a plain card on the next, a rule and a number on
 * the third, which is why the lifecycle read as six screens by six designers.
 * They now all open with this, and only the field colour and the figure change.
 *
 * The composition is the promo banners', because a state card has the banners'
 * job: one value, one verdict, one action. That means two zones rather than a
 * stack. The copy is clamped to the left, the figure is pinned right and
 * oversized, and the headline is pushed to the bottom baseline by mt-auto so
 * the card has a floor instead of a column of type drifting in the middle.
 *
 * The figure is aria-hidden. It is artwork, not prose, and it is spoken once in
 * the copy zone instead, so a screen reader gets the sentence and not a picture
 * read out of order.
 */
function ArenaFieldCard({
  tone,
  eyebrow,
  strip,
  chip,
  progress,
  foot,
  brandMark = false,
  headline,
  caption,
  figure,
  figureCaption,
  figureLive = false,
  reveal = false,
  radar = false,
  className,
  children,
  ...props
}: ArenaFieldCardProps) {
  const field = ARENA_FIELD[tone]
  const accent = ARENA_FIELD_ACCENT[tone]
  const framed = Boolean(strip || foot)

  const body = (
    <div
      className={cn(
        // The hairline ring is the heavy-pro edge: on a near-black field the
        // card needs a machined boundary or it dissolves into the page. When
        // the card is framed by the strip and the foot, the frame owns both.
        "relative isolate w-full overflow-hidden",
        !framed && "min-h-[152px] rounded-[14px] ring-1 ring-inset ring-white-100",
        !framed && className
      )}
      style={{ background: field.background }}
      {...(framed ? {} : props)}
    >
      {/* Directional light: warm up-right behind the figure, darkening
          down-left so the headline holds its contrast against the brightest
          corner of the field. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `${field.glow}, linear-gradient(118deg, hsl(var(--black-500)) 0%, transparent 42%)`,
        }}
      />
      {/* The board banners' halftone dot grid, same values, so the lifecycle
          cards and the promo banners read as one printed surface. Overlay
          blend keeps it texture on the colour fields and near-nothing on the
          dark one. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.17,
          mixBlendMode: "overlay",
          // The grid lives on the figure's side and dies toward the copy.
          maskImage: "linear-gradient(to left, black 0%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(to left, black 0%, transparent 80%)",
        }}
      />
      {/* The brand in the ground: the mark oversized past the corner, at an
          opacity that reads as material rather than a watermark, and the
          field's accent blooming from the opposite corner so the two diagonals
          answer each other. */}
      {brandMark && (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: `radial-gradient(120% 150% at 6% 100%, ${accent.bloom} 0%, transparent 52%)`,
            }}
          />
          {/* Sized and placed to the reference card: the mark owns the right
              half of the field behind the figure, upright, with only its
              bottom edge slipping past the card. */}
          {/* Anchored from the top, not the bottom: growing the mark must
              never push it up into the strip, so the overflow all happens
              past the card's foot, where the clip crops it. */}
          <ArenaMark
            className={cn(
              "pointer-events-none absolute -top-1 aspect-[40.7/31]",
              // The brand and win fields run the mark larger and pushed
              // further past the right edge.
              tone === "win" || tone === "brand"
                ? "-right-[8%] w-[61%] min-w-[248px]"
                : "-right-[3%] w-[50%] min-w-[205px]"
            )}
            alphaBoost={tone === "brand" || tone === "win" ? 0.05 : 0}
          />
        </>
      )}

      {/* Figure and copy share one relative zone, so the figure centres on
          the copy — the headline's own rows — and never drifts down into the
          progress rail's extra height. */}
      <div className="relative">
      {figure && (
        <>
          {/* No plate behind the figure any more: on the black field the
              number stands on the ground itself, and a glass rectangle read
              as a box someone forgot to fill. */}
          {/* One shape at one speed that never changes, so it stays ambient: it
              cannot be mistaken for progress, because no progress is knowable
              while the search runs. */}
          {radar && (
            <span
              aria-hidden
              className="pointer-events-none absolute right-[14px] top-1/2 hidden size-[136px] -translate-y-1/2 sm:block"
            >
              <span className="absolute inset-0 rounded-full border border-gold/20" />
              <span className="absolute inset-[20%] rounded-full border border-gold/[0.14]" />
              <span className="absolute inset-[40%] rounded-full border border-gold/10" />
              <span
                className="absolute inset-0 animate-spin rounded-full motion-reduce:animate-none"
                style={{
                  animationDuration: "3.6s",
                  background:
                    "conic-gradient(from 0deg, hsl(var(--gold) / 0.32) 0deg, hsl(var(--gold) / 0.07) 30deg, transparent 66deg, transparent 360deg)",
                  mask: "radial-gradient(circle, black 0%, black 52%, transparent 78%)",
                  WebkitMask: "radial-gradient(circle, black 0%, black 52%, transparent 78%)",
                }}
              />
              {/* The mark at the centre of the sweep, in glass — but only
                  while the card has no figure: with one, the number takes the
                  centre and two objects in one bullseye is one too many.
                  Masked rather than drawn as an <img> because the file is a
                  fixed pink and pink on the pink field is invisible; the mask
                  lets the shape be lit by the field it sits on instead of
                  carrying its own colour onto it. */}
              {!figure && (
              <span
                className="absolute left-1/2 top-1/2 h-[42px] w-[55px] -translate-x-1/2 -translate-y-1/2 backdrop-blur-[2px]"
                style={{
                  maskImage: "url(/picks-mark.svg)",
                  WebkitMaskImage: "url(/picks-mark.svg)",
                  maskSize: "contain",
                  WebkitMaskSize: "contain",
                  maskRepeat: "no-repeat",
                  WebkitMaskRepeat: "no-repeat",
                  maskPosition: "center",
                  WebkitMaskPosition: "center",
                  background: `linear-gradient(140deg, ${
                    "hsl(var(--white-500))"
                  } 0%, ${
                    "hsl(var(--white-100))"
                  } 52%, ${
                    "hsl(var(--white-400))"
                  } 100%)`,
                }}
              />
              )}
            </span>
          )}

          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute top-1/2 flex -translate-y-1/2 flex-col",
              // With the radar running, the figure is its bullseye: same
              // centre, same width, instead of a second object drifting over
              // the rings' left edge.
              radar
                ? "right-[14px] w-[136px] items-center"
                : "right-4 items-end sm:right-5"
            )}
          >
            {/* 1:1 the board banners' number hero ("5%", "$20."): font-bold,
                leading-none, tracking-[-0.04em], clamp(44px, 14vw, 76px).
                The cap steps down as the figure gets longer — "$4,335" at the
                "$400" size runs into the copy zone. */}
            <span
              suppressHydrationWarning
              className={cn(
                reveal && "arena-result-in [--arena-result-delay:120ms]",
                "font-open-sauce font-bold leading-none tracking-[-0.04em] tabular-nums",
                figureLive ? "text-white" : "text-gold"
              )}
              style={{
                // 10vw, not 14vw: at 390pt phones 14vw (54.6px) overruns the
                // figure's 44% zone and the 136px radar column. At >=768px
                // 10vw already exceeds every cap, so desktop is unchanged.
                fontSize: `clamp(34px, 10vw, ${
                  figure && figure.length > 6 ? 46 : figure && figure.length > 4 ? 58 : 76
                }px)`,
              }}
            >
              {figure}
            </span>
            {figureCaption && (
              <span
                className={cn(
                  // 1:1 the banners' "Up to" hang: 9px, 0.18em, white/55.
                  "mt-1 text-[9px] font-semibold uppercase tracking-[0.18em]",
                  "text-white/55"
                )}
              >
                {figureCaption}
              </span>
            )}
          </span>
        </>
      )}

      <div
        className={cn(
          "relative z-[4] flex flex-col p-4 sm:p-5",
          // A framed card's height is its content — but never shorter than
          // the banner-scale figure that centres on this zone, or the
          // figure's caption spills into the rail below. 118px fits the 76px
          // number plus its caption with air. The bare field keeps the full
          // banner-height floor.
          framed ? figure && "min-h-[118px]" : "min-h-[152px]"
        )}
        // The copy zone ends where the figure's real estate begins, not at an
        // arbitrary fraction: the flat 56% was clipping captions that had a
        // clear run at the space left of the radar. 150px is the radar's exact
        // footprint (136px of rings + its 14px inset).
        style={{
          maxWidth: figure ? (radar ? "calc(100% - 150px)" : "56%") : "100%",
        }}
      >
        {/* Under a strip, no eyebrow: the strip already names where we are.
            The chip is different — it is the card's live status, which neither
            the strip nor the headline states in the present tense. */}
        {!strip && (
          <span className={cn("block", ARENA_EYEBROW, "text-white-700")}>
            {eyebrow}
          </span>
        )}
        {chip && (
          <span
            className={cn(
              "mb-2 inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-[5px]",
              // Black-on-black disappears; the dark field lifts the pill instead.
              tone === "dark" ? "bg-white-100" : "bg-grey-950/[0.18]"
            )}
          >
            <span aria-hidden className="size-[6px] rounded-full bg-gold" />
            <span className="font-mono text-[9px] font-medium uppercase leading-none tracking-[0.18em] text-white">
              {chip}
            </span>
          </span>
        )}
        {/* 1:1 the board banners' headline ("Rakeback is live."): font-bold,
            leading-[1.05], tracking-tight, 24px cap with the same clamp. */}
        <h2
          className={cn(
            // mt-auto is the bare field's floor push; a framed card's copy
            // starts where the strip ends.
            framed ? "mt-0" : "mt-auto",
            "font-open-sauce font-bold leading-[1.05] tracking-tight text-white"
          )}
          style={{ fontSize: "clamp(18px, 4vw, 24px)" }}
        >
          {headline}
        </h2>
        {caption && (
          <p
            suppressHydrationWarning
            className={cn(
              // font-normal, not the doc's font-medium: Open Sauce ships
              // 400/600/900 only and a 500 would be synthesized.
              "mt-1.5 font-open-sauce text-[12px] font-normal leading-snug",
              "text-white-700"
            )}
          >
            {caption}
          </p>
        )}
        {figure && (
          <span className="sr-only">
            {figure}
            {figureCaption ? `, ${figureCaption}` : ""}
          </span>
        )}
        {children}
      </div>
      </div>

      {/* The rail spans the full field, outside the clamped copy zone: it is
          a fact about the group, not a line of copy. */}
      {progress && (
        <div className="relative z-[4] -mt-1.5 px-4 pb-3 sm:px-5 sm:pb-3.5">
          <div className="relative">
            <div className="h-[6px] w-full overflow-hidden rounded-full bg-white-100">
              <span
                className="block h-full rounded-full bg-gold"
                style={{
                  width: `${Math.min(100, Math.max(0, (progress.value / progress.max) * 100))}%`,
                }}
              />
            </div>
            {progress.threshold && progress.value < progress.threshold.value && (
              <span
                className="group/threshold absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${Math.min(100, (progress.threshold.value / progress.max) * 100)}%`,
                }}
              >
                {/* A tick, not a dot: a vertical line reads as a mark ON the
                    rail rather than a point floating over it. */}
                <span className="block h-[12px] w-[2px] rounded-full bg-gold" />
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max max-w-[240px] -translate-x-1/2 rounded-md bg-grey-900 px-2.5 py-1.5 text-[11px] leading-snug text-white opacity-0 ring-1 ring-inset ring-white-200 transition-opacity group-hover/threshold:opacity-100 group-active/threshold:opacity-100">
                  {progress.threshold.explain}
                </span>
              </span>
            )}
          </div>
          {(progress.left || progress.right) && (
            <div className="mt-2 flex items-baseline justify-between gap-3">
              {progress.left && (
                <span className="text-[12px] font-semibold tracking-tight text-white">
                  {progress.left}
                </span>
              )}
              {progress.right && (
                <span className="ml-auto text-[12px] font-semibold tracking-tight text-white-700">
                  {progress.right}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )

  if (!framed) return body

  return (
    <div
      className={cn("overflow-hidden rounded-[14px] ring-1 ring-inset ring-white-100", className)}
      {...props}
    >
      {strip && (
        <div
          className={cn(
            "flex items-center justify-between px-4 py-1.5",
            // On the dark field a gold strip announces a win that did not
            // happen, so the strip goes quiet with the field.
            tone === "dark" ? "bg-grey-900" : "bg-gold"
          )}
        >
          <span className={cn(ARENA_STRIP_TEXT, tone === "dark" && "text-grey-300")}>
            {strip.left}
          </span>
          <span className={cn(ARENA_STRIP_TEXT, tone === "dark" && "text-grey-300")}>
            {strip.right}
          </span>
        </div>
      )}
      {body}
      {foot && (
        <div
          className="flex items-center justify-between gap-3 border-t border-white-100 px-4 py-2.5"
          style={{ background: "hsl(0 0% 4%)" }}
        >
          <span className="text-[12px] leading-tight text-white-700">{foot.left}</span>
          <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-semibold tracking-tight text-white">
            {foot.usdcIcon !== false && <UsdcIcon className="size-4" aria-hidden />}
            {foot.right}
          </span>
        </div>
      )}
    </div>
  )
}

/* =============================================================================
   ScoreValue
   ============================================================================= */

interface ScoreValueProps extends React.ComponentProps<"div"> {
  /** The Arena score. Always rendered to two decimals. */
  value: number
  /** Running score during play, or the settled one. Defaults to final. */
  variant?: "projected" | "final"
  /**
   * hero: Pixter Terminal, one per screen. 44px settled, 34px projected.
   * row: a score cell in the group table.
   * inline: a score inside a sentence or a ledger reason line.
   */
  size?: "hero" | "row" | "inline"
  /** Row emphasis: the reader's own row is white, everyone else is grey-100. */
  emphasis?: "you" | "other"
  /**
   * Show the "Projected" / "Final score" label. Defaults on for hero (the
   * distinction is the whole point of the hero) and off for table cells,
   * where a per-row label would be 64 repetitions of the same word.
   */
  showLabel?: boolean
  /** Override the label text. Ignored when showLabel is false. */
  label?: string
}

/**
 * The Arena score. Projected and final are told apart three ways, so the
 * distinction survives a screenshot, a colour-blind reader and a glance:
 *
 *   1. the stated label, "Projected" or "Final score";
 *   2. SIZE. A settled score is the record and takes the full 44px. A
 *      projected one is provisional, sits a step down at 34px, and is not the
 *      hero of its screen; before settlement the prize ledger is.
 *   3. MOTION. A projected score can move while the reader is looking at it,
 *      and it is marked when it does. A final score is stone and never ticks.
 */
function ScoreValue({
  value,
  variant = "final",
  size = "row",
  emphasis = "other",
  showLabel,
  label,
  className,
  ...props
}: ScoreValueProps) {
  const isProjected = variant === "projected"
  const withLabel = showLabel ?? size === "hero"
  const labelText = label ?? (isProjected ? "Projected" : "Final score")

  // Counted rather than keyed off the value itself, so the first paint is
  // silent: an animation on mount would mark "this exists", not "this changed".
  const [tick, setTick] = React.useState(0)
  const previousValue = React.useRef(value)
  React.useEffect(() => {
    if (previousValue.current === value) return
    previousValue.current = value
    setTick((count) => count + 1)
  }, [value])

  // Only the reader's own live score is worth marking. A 64-row table all
  // flashing at once is noise, and a settled score cannot change at all.
  const marksChange = isProjected && (size === "hero" || emphasis === "you")

  // Pixter Terminal ships a single 700 file. Requesting anything else lets the
  // browser synthesise, so the weight is pinned to the registered one.
  const numberClass =
    size === "hero"
      ? cn(
          "font-pixter-terminal [font-weight:700] leading-none tracking-[-0.02em]",
          isProjected ? "text-[34px] text-grey-50" : "text-[44px] text-white"
        )
      : size === "row"
        ? cn(
            "text-[15px] font-semibold tabular-nums tracking-tight",
            emphasis === "you" ? "text-white" : "text-grey-100",
            isProjected && "text-grey-200"
          )
        : cn(
            "text-[13px] font-semibold tabular-nums tracking-tight",
            emphasis === "you" ? "text-white" : "text-grey-200"
          )

  return (
    <div className={cn("flex flex-col gap-1", className)} {...props}>
      {/* Deliberately NOT the mono eyebrow. This label is a caption bound to
          the number under it, not a block heading, and callers pass sentences
          ("If every pick hits") that uppercase mono would widen past the
          column they sit in. Against a 44px figure, 11px is already chrome. */}
      {withLabel && (
        <span className="text-[11px] leading-none text-grey-300">{labelText}</span>
      )}
      {/* Re-keyed on every change so the tick replays rather than firing once. */}
      <span
        key={tick}
        className={cn(numberClass, marksChange && tick > 0 && "arena-value-tick")}
      >
        {value.toFixed(2)}
      </span>
    </div>
  )
}

/* =============================================================================
   GateMeter
   ============================================================================= */

interface GateMeterProps extends React.ComponentProps<"div"> {
  /** Lineups in the group right now. */
  lineupCount: number
  /** Picks per lineup in this group. Used for the copy, not for any threshold. */
  pickCount: number
  /**
   * Server-owned. At or below this many lineups the group does not run and
   * every entry fee is returned. Never derived on the client.
   */
  refundAtOrBelow: number
  /** Server-owned. At or above this many lineups the top score prize runs. */
  topScoreLiveAt: number
  /** Group ceiling. 64 everywhere today. */
  maxSize?: number
  /** After lock the bands are settled fact; before lock they can still move. */
  locked?: boolean
}

/**
 * The group-fill ladder. Shows where the group sits against its refund floor
 * and its top-score gate, with all three outcomes stated rather than one
 * highlighted and two hidden: a player has to be able to read what happens to
 * their money at the count the group actually has.
 *
 * The track is a position marker, not a progress bar. Nothing here is a fill
 * toward a goal, because a bigger group is not automatically a better one for
 * the reader; the two ticks are the only two counts that change the outcome.
 */
function GateMeter({
  lineupCount,
  pickCount,
  refundAtOrBelow,
  topScoreLiveAt,
  maxSize = MAX_GROUP_SIZE,
  locked = true,
  className,
  ...props
}: GateMeterProps) {
  const active = activeGateBand(lineupCount, refundAtOrBelow, topScoreLiveAt)
  const bands = buildGateBands(refundAtOrBelow, topScoreLiveAt, maxSize)
  const mediumFrom = refundAtOrBelow + 1

  const totalWeight = bands.reduce((sum, band) => sum + band.weight, 0)
  const markerPercent = Math.min(
    100,
    Math.max(0, (lineupCount / Math.max(maxSize, 1)) * 100)
  )

  // A footnote only earns the rule above it when it says something the ladder
  // does not. Naming the live band restates the band already set in white with
  // an accent rule, and the shortfall to the top-score gate is a target only
  // while lineups can still join: once the group is locked the count cannot
  // move, so it would price a door that has already closed.
  const footnote =
    active === "refund"
      ? `Needs ${mediumFrom} lineups to run.`
      : active === "medium" && !locked
        ? `${topScoreLiveAt - lineupCount} more lineups for the top score prize.`
        : null

  // The band rule says WHICH prize the group's current size switches on, so the
  // colour is a fact rather than a highlight: grey where the money only comes
  // back, the perfect-lineup accent where that branch alone runs, the top-score
  // identity where both do. Only one band is ever lit, so one hue is on screen.
  const bandRule: Record<string, string> = {
    refund: "bg-grey-200",
    medium: ARENA_BRANCH.perfect.rule,
    live: ARENA_BRANCH.top.rule,
  }

  return (
    <div className={cn(ARENA_SURFACE.card, className)} {...props}>
      <div className="flex items-baseline justify-between gap-3">
        <span className={ARENA_EYEBROW}>Group size</span>
        {/* The count is the one fact on this card that decides an outcome, so
            it takes a size step of its own. It stays well under the screen's
            hero figure: this card supports the money, it is not the money. */}
        <span className="text-[12px] text-grey-300">
          <span className="text-[15px] font-semibold tabular-nums text-white">
            {lineupCount}
          </span>
          {" of "}
          <span className="tabular-nums">{maxSize}</span> lineups
        </span>
      </div>

      {/* Only the pre-lock line survives: it says the count is still moving,
       * which a static number cannot. The locked wording repeated the pick
       * count, the lineup count and the lock state that the header above and
       * the group meta row beside it already carry. */}
      {!locked && (
        <p className="mt-2 text-[13px] leading-relaxed text-grey-300">
          Still open to more {pickCount}-pick lineups.
        </p>
      )}

      {/* Position track. Segment widths are the real ranges, so the marker sits
       * where the group genuinely is between the two gates. */}
      <div className="relative mt-4 mb-4 h-[3px] w-full">
        <div className="flex h-full w-full gap-px bg-grey-800">
          {bands.map((band) => (
            <div
              key={band.key}
              style={{ flexGrow: band.weight / totalWeight }}
              className={cn(
                "h-full",
                band.key === active ? bandRule[band.key] : "bg-grey-800"
              )}
            />
          ))}
        </div>
        <div
          aria-hidden
          className="absolute top-[-3px] h-[9px] w-[2px] -translate-x-1/2 bg-white"
          style={{ left: `${markerPercent}%` }}
        />
      </div>

      <div>
        {bands.map((band) => {
          const isActive = band.key === active
          return (
            <div
              key={band.key}
              className={cn(
                "flex gap-3 border-b border-grey-800 py-3 last:border-b-0",
                !isActive && "opacity-60"
              )}
            >
              <div
                aria-hidden
                className={cn(
                  "w-[2px] shrink-0 rounded-full",
                  isActive ? bandRule[band.key] : "bg-grey-800"
                )}
              />
              <div className="grid flex-1 grid-cols-[1fr_auto] gap-x-3">
                <span
                  className={cn(
                    "text-[13px] font-semibold tracking-tight",
                    isActive ? "text-white" : "text-grey-200"
                  )}
                >
                  {band.title}
                </span>
                <span className="text-[12px] tabular-nums text-grey-300">
                  {band.range}
                </span>
                <span className="col-span-2 mt-1 text-[12px] leading-relaxed text-grey-300">
                  {band.detail}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {footnote && (
        <p className="mt-3 border-t border-grey-800 pt-3 text-[12px] leading-relaxed text-grey-200">
          {footnote}
        </p>
      )}
    </div>
  )
}

/* =============================================================================
   TierBadge
   ============================================================================= */

interface TierBadgeProps extends Omit<React.ComponentProps<"span">, "onClick"> {
  /** Experience tier as the backend names it, e.g. "intermediate". */
  tier: string
  size?: "sm" | "default"
  /**
   * When passed, the badge becomes a tap target that opens the caller's
   * explanation of what the tier is based on. A label a player never chose
   * and cannot change has to be explainable somewhere.
   */
  onExplain?: () => void
}

/**
 * The experience tier, rendered as a word rather than a shouted micro-cap.
 * This is chrome: it labels a grouping dimension the player never chose, so it
 * sits on the quiet border and steps up only when it is a tap target under the
 * cursor.
 */
function TierBadge({
  tier,
  size = "default",
  onExplain,
  className,
  ...props
}: TierBadgeProps) {
  // The band name alone ("Rookie") sat unlabelled at the end of a run of five
  // slash-separated facts, where it reads as a difficulty or a personal rank.
  // The noun is what makes it a grouping dimension rather than a verdict.
  const label = `${toSentenceCase(tier)} tier`

  return (
    <Badge
      variant="outline"
      size={size}
      asChild={Boolean(onExplain)}
      // The machined tag: squared corners, hairline ring, faint lift off the
      // panel, and the ribbon's mono voice. It reads as a stamped spec label
      // rather than a word drifting at the end of the row.
      className={cn(
        "rounded-[6px] border-white-200 bg-white-50 font-mono text-[9px] font-medium uppercase tracking-[0.14em] text-grey-200",
        onExplain && "transition-colors hover:border-white-300 hover:text-white",
        className
      )}
      {...props}
    >
      {onExplain ? (
        <button type="button" onClick={onExplain}>
          {label}
        </button>
      ) : (
        label
      )}
    </Badge>
  )
}

/* =============================================================================
   GroupMetaRow
   ============================================================================= */

interface GroupMetaRowProps extends React.ComponentProps<"div"> {
  pickCount: number
  /** Day the last game in the group finishes, e.g. "Saturday". */
  settlementDay: string
  /** Omit while the group is still filling: the filling card's figure already
      carries the live count, and this row restated it two hundred pixels
      below. From the lock onward the size is a settled fact and belongs here. */
  lineupCount?: number
  maxSize?: number
  /** Omit to leave the tier off the row entirely. */
  tier?: string
  onExplainTier?: () => void
  /** Group lifecycle word, e.g. "waiting" | "locked" | "live" | "settled". */
  state?: string
}

/**
 * The three facts that identify a group: what it is, when it settles, how big
 * it is. Chrome, and priced as chrome: nothing in this row is money and nothing
 * in it is the hero, so the emphasised values sit at grey-100 rather than pure
 * white, one step under whatever figure the screen is actually about.
 */
function GroupMetaRow({
  pickCount,
  settlementDay,
  lineupCount,
  maxSize = MAX_GROUP_SIZE,
  tier,
  onExplainTier,
  state,
  className,
  ...props
}: GroupMetaRowProps) {
  return (
    <div className={cn("flex items-center gap-3", className)} {...props}>
      {/* One line, always: the facts never wrap; on a column too narrow for
          all of them the row clips at its end rather than stacking. */}
      <div className="flex min-w-0 flex-1 items-center gap-x-2 overflow-hidden whitespace-nowrap text-[12px] text-grey-300">
        <span className="font-semibold text-grey-100">{pickCount}-pick lineups</span>
        {lineupCount !== undefined && (
          <>
            <span aria-hidden className="text-grey-600">
              /
            </span>
            <span>
              <span className="font-semibold tabular-nums text-grey-100">{lineupCount}</span>
              {" of "}
              <span className="tabular-nums">{maxSize}</span> lineups
            </span>
          </>
        )}
        {state && (
          <>
            <span aria-hidden className="text-grey-600">
              /
            </span>
            <span>{toSentenceCase(state)}</span>
          </>
        )}
      </div>
      {/* Pinned to the row's right edge: the tier is a property of the group,
          not the fourth item in the sentence. */}
      {tier && (
        <TierBadge tier={tier} size="sm" onExplain={onExplainTier} className="shrink-0" />
      )}
    </div>
  )
}

/* =============================================================================
   RefundNotice
   ============================================================================= */

type RefundReason = "not-matched" | "waited-too-long" | "group-too-small" | "no-score"

interface RefundNoticeProps extends React.ComponentProps<"div"> {
  /**
   * Which refund happened. Omit to render the full-refund guarantees as a
   * block, which is what the waiting screen shows: nothing has gone wrong yet,
   * these are the conditions that return the entry fee in full.
   */
  reason?: RefundReason
  /**
   * Which guarantees the block lists. Defaults to the group-size floor alone,
   * because on the waiting screen the other two are the matchmaker's own
   * deadlines and the reader is already looking at their min(): the clock
   * beside this block prints one "matched or refunded by" time and names both
   * outcomes. Which of the two timers fired changes nothing they do, see or
   * receive, and only becomes information after a refund, where the
   * reason-specific branch below states it. The explainer, which teaches the
   * mechanic rather than reporting a state, passes all three.
   */
  guarantees?: RefundReason[]
  /** "will" before settlement, "did" after. Defaults to "did". */
  tense?: "will" | "did"
  /** The player's own entry fee. Stated alongside every returned amount. */
  entryFeeUsd?: number
  /** What actually came back. Defaults to the full entry fee. */
  returnedUsd?: number
  /** Lineups in the group, used by the group-too-small copy when known. */
  lineupCount?: number
  /** Lineups the group needed to run, used by the group-too-small copy. */
  requiredLineups?: number
}

// Conditions only. The block heading states the outcome once, so repeating
// "entry fee returned in full" on all three rows taught the same rule a fourth
// time in a list the reader is scanning for the conditions.
const REFUND_GUARANTEES: { key: RefundReason; text: string }[] = [
  { key: "not-matched", text: "Not matched by your first game." },
  { key: "waited-too-long", text: "No group after 4 hours." },
  { key: "group-too-small", text: "Group too small to run." },
]

/**
 * Refunds, with the tone the amount deserves. Neither branch is a win, so
 * neither ever takes --win: money coming back is not money earned.
 *
 * The difference between the two is carried by the figure itself, not by the
 * sentence under it. A full refund is the whole entry fee returning, so it is
 * the hero of the card: hero scale, white, the biggest thing on the screen. A
 * 10% return is not a refund in any meaningful sense, so it is demoted on both
 * axes at once, to the supporting size and to grey, and the badge above it goes
 * quiet while the full-refund badge goes bright. Two figures a size apart say
 * "you are whole" and "you lost most of your entry" before a word is read.
 */
function RefundNotice({
  reason,
  guarantees = ["group-too-small"],
  tense = "did",
  entryFeeUsd,
  returnedUsd,
  lineupCount,
  requiredLineups,
  className,
  ...props
}: RefundNoticeProps) {
  // No reason given: the pre-settlement guarantee block.
  if (!reason) {
    return (
      <div className={cn(ARENA_SURFACE.card, className)} {...props}>
        <span className={ARENA_EYEBROW}>Refunded in full</span>
        <div className="mt-2">
          {REFUND_GUARANTEES.filter((guarantee) =>
            guarantees.includes(guarantee.key)
          ).map((guarantee) => (
            <p
              key={guarantee.key}
              className="border-b border-grey-800 py-2.5 text-[13px] leading-relaxed text-grey-200 last:border-b-0 last:pb-0"
            >
              {/* The threshold, where the caller knows it. The gate that PAYS is
                  quantified on the prize ledger above, so leaving the gate that
                  REFUNDS unnumbered beside it made one of the two rules look
                  softer than it is. */}
              {guarantee.key === "group-too-small" && requiredLineups
                ? `Fewer than ${requiredLineups} lineups join.`
                : guarantee.text}
            </p>
          ))}
        </div>
      </div>
    )
  }

  const isPartial = reason === "no-score"
  const amount = returnedUsd ?? entryFeeUsd
  const past = tense === "did"

  const headline = isPartial
    ? "No one scored"
    : reason === "not-matched"
      ? "No group formed"
      : reason === "waited-too-long"
        ? "No group formed in time"
        : "Group did not run"

  // The condition alone. The badge names the outcome and the amount below prints
  // it, so restating "returned in full" here was the third telling on one card.
  const explanation = isPartial
    ? "Every pick in this group missed."
    : reason === "not-matched"
      ? "Not enough similar lineups before your first game."
      : reason === "waited-too-long"
        ? "Waited more than 4 hours."
        : lineupCount !== undefined && requiredLineups !== undefined
          ? `${lineupCount} lineups joined, ${requiredLineups} needed to run.`
          : "Too few lineups to run."

  // The amount above is the figure. This line carries only what the figure
  // cannot: which entry fee a partial return came out of, or where a full one
  // went.
  const moneyLine =
    amount === undefined
      ? null
      : isPartial && entryFeeUsd !== undefined
        ? `Of your ${formatUsd(entryFeeUsd)} entry.`
        : past
          ? "Returned to your balance."
          : "Returns to your balance."

  // A stated refund is the outcome of its screen, so it takes the raised shell.
  // The guarantee block above is a rule, not an outcome, and stays recessed.
  return (
    <div className={cn(ARENA_SURFACE.hero, className)} {...props}>
      <div className="flex items-start justify-between gap-3">
        <span className="font-open-sauce text-[15px] font-semibold tracking-tight text-white">
          {headline}
        </span>
        <Badge
          variant="outline"
          size="sm"
          className={cn(
            "font-semibold",
            isPartial
              ? "border-grey-800 text-grey-300"
              : "border-white-300 text-white"
          )}
        >
          {isPartial ? "10% returned" : "Refunded in full"}
        </Badge>
      </div>

      {amount !== undefined && (
        <p
          className={cn(
            "mt-3 [--arena-result-delay:120ms] arena-result-in",
            isPartial
              ? arenaMoney("major", "live", "text-grey-200")
              : arenaMoney("hero")
          )}
        >
          {formatUsd(amount)}
        </p>
      )}

      {moneyLine && (
        <p className="mt-1 text-[13px] leading-relaxed text-grey-200">{moneyLine}</p>
      )}

      <p className="mt-2 text-[13px] leading-relaxed text-grey-300">{explanation}</p>
    </div>
  )
}

/* =============================================================================
   ArenaSectionHeading
   ============================================================================= */

interface ArenaSectionHeadingProps extends React.ComponentProps<"div"> {
  title: string
  /** Mono eyebrow above the title. One per block, at most one accented per screen. */
  eyebrow?: string
  /** Renders the eyebrow in a branch identity. Reserve it for a branch marker. */
  accentEyebrow?: boolean
  /**
   * Which branch the accented eyebrow belongs to: "perfect" is the guaranteed
   * branch, "top" the contested one. Ignored when accentEyebrow is false, and
   * never applied to the title or the description, which stay on the text ramp.
   */
  accentBranch?: keyof typeof ARENA_BRANCH
  description?: string
  /** Right-aligned control, e.g. a text-only "How Arena works" trigger. */
  action?: React.ReactNode
  /** page: Open Sauce page title. block: the standard 15px section heading. */
  level?: "page" | "block"
}

/** The shared heading treatment for every Arena block. */
function ArenaSectionHeading({
  title,
  eyebrow,
  accentEyebrow = false,
  accentBranch = "perfect",
  description,
  action,
  level = "block",
  className,
  ...props
}: ArenaSectionHeadingProps) {
  return (
    <div className={cn("flex items-start justify-between gap-3", className)} {...props}>
      <div className="min-w-0">
        {eyebrow && (
          <span
            className={cn(
              "block",
              ARENA_EYEBROW,
              accentEyebrow && ARENA_BRANCH[accentBranch].tint
            )}
          >
            {eyebrow}
          </span>
        )}
        {level === "page" ? (
          <h1
            className={cn(
              "font-open-sauce text-[26px] font-black tracking-[-0.03em] text-white md:text-[32px]",
              eyebrow && "mt-1.5"
            )}
          >
            {title}
          </h1>
        ) : (
          <h2
            className={cn(
              "font-open-sauce text-[15px] font-semibold tracking-tight text-white",
              eyebrow && "mt-1.5"
            )}
          >
            {title}
          </h2>
        )}
        {description && (
          <p className="mt-1.5 text-[13px] leading-relaxed text-grey-300">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export {
  ArenaFieldCard,
  ArenaMark,
  ARENA_FIELD,
  ScoreValue,
  GateMeter,
  TierBadge,
  GroupMetaRow,
  RefundNotice,
  ArenaSectionHeading,
  ArenaBrandedStripes,
  ARENA_SURFACE,
  ARENA_MONEY,
  ARENA_MONEY_TONE,
  ARENA_BRANCH,
  ARENA_EYEBROW,
  ARENA_TEXT_ACTION,
  arenaMoney,
}
